import {
  getFormFieldValidations,
  isEmailValid,
  isValidCountryCode,
  isValidOptionalPhone,
  normalizeCountryCode,
} from '@avalara/bat-apps-shared/lib/form-validation';

import { requiredFormFields } from '../reducers/helpers/forms-constants';

import type { SharedSignupState } from '@avalara/types/reducer-states';

export const offerGetStartedFields = ['firstName', 'lastName', 'email', 'companyName'] as const;
export const offerCompanyAddressFields = ['street1', 'city', 'state', 'postalCode', 'country', 'phone'] as const;

export const getOfferGetStartedValidationFields = (isPasswordRequired?: boolean): Array<string> =>
  isPasswordRequired ? [...offerGetStartedFields, 'password'] : [...offerGetStartedFields];

export const getOfferCompanyValidationFields = (additionalRequiredFields: Array<string> = []): Array<string> => [
  ...offerCompanyAddressFields,
  ...additionalRequiredFields,
];

export const getSignupValidationFieldNames = (state: SharedSignupState): Array<string> => [
  ...Object.keys(state.signupForm),
  ...(state.additionalRequiredFields ?? []),
];

export const isFieldMissing = (state: SharedSignupState) => (field: string) => {
  const additionalRequiredFields = state.additionalRequiredFields ?? [];
  const isPhoneOptional = field === 'phone' && !additionalRequiredFields.includes(field);
  if (isPhoneOptional) return false;

  return (
    !state.signupForm[field as keyof typeof state.signupForm]?.trim() &&
    (requiredFormFields.includes(field) || additionalRequiredFields.includes(field))
  );
};

export const isFieldInvalid =
  ({ signupForm }: SharedSignupState) =>
  (field: string) => {
    if (field === 'street1' && [signupForm.street1, signupForm.street2].filter(Boolean).join(' ').length > 50)
      return true;

    const normalizedCountry = normalizeCountryCode(signupForm.country) ?? '';

    // NOTE (Sasha): Given the non-standard formats that other countries set for
    // postalCode and the vast number of regions for other countries, skip format
    // validation if not US or Canada while still requiring a value.
    if (normalizedCountry !== 'US' && normalizedCountry !== 'CA' && (field === 'postalCode' || field === 'state'))
      return false;

    if (field === 'email') return !isEmailValid(signupForm.email);
    if (field === 'phone') return !isValidOptionalPhone(signupForm.phone);
    if (field === 'country') return !isValidCountryCode(signupForm.country);

    const fieldValidations = getFormFieldValidations(normalizedCountry || signupForm.country);
    type FieldKey = keyof typeof fieldValidations;
    type SignupFormKey = keyof typeof signupForm;

    return Boolean(
      fieldValidations[field as FieldKey] &&
        !fieldValidations[field as FieldKey].test(signupForm[field as SignupFormKey] || ''),
    );
  };

const isFieldMissingOrInvalid = (state: SharedSignupState) => (field: string) => {
  const normalValidation = isFieldMissing(state)(field) || isFieldInvalid(state)(field);
  if (field === 'email') {
    return normalValidation || !state.emailIsVerified;
  }
  if (field === 'password') {
    return isFieldInvalid(state)(field);
  }
  return normalValidation;
};

// Returns array of all invalid fields (missing or failing a regex validation)
export const getInvalidFields = (state: SharedSignupState): Array<string> =>
  getSignupValidationFieldNames(state).filter(isFieldMissingOrInvalid(state));

// Returns true if field is missing or invalid, *unless we've yet to visit it*
export const isFieldVisitedAndInvalid = (state: SharedSignupState, field: string) => {
  if (!state.fieldsClearedForValidation[field]) return false;

  return isFieldMissingOrInvalid(state)(field);
};

const areSomeFormFieldsNotCompleted =
  (fields: Array<string>) =>
  (state: SharedSignupState): boolean =>
    fields.some(isFieldMissingOrInvalid(state));

// Same function as isFormIncomplete, but specific to page 1 of avatax signup
export const isFormOneIncomplete = areSomeFormFieldsNotCompleted(['firstName', 'lastName', 'email', 'phone']);

// Same function as isFormIncomplete, but specific to page 1 of offer signup
export const isOfferFormOneIncomplete = (state: SharedSignupState, isPasswordRequired?: boolean): boolean =>
  isPasswordRequired
    ? areSomeFormFieldsNotCompleted(['firstName', 'lastName', 'email', 'companyName', 'password'])(state)
    : areSomeFormFieldsNotCompleted(['firstName', 'lastName', 'email', 'companyName'])(state);

export const esbFormOneFields = ['companyName', 'country', 'street1', 'city', 'state', 'postalCode', 'phone'];
// Same function as isFormIncomplete, but specific to ESB signup
export const isEsbFormOneIncomplete = areSomeFormFieldsNotCompleted(esbFormOneFields);

export const isEsbFormTwoIncomplete = areSomeFormFieldsNotCompleted(['firstName', 'lastName', 'email', 'password']);

// Helper to track if entire form (regardless of how many pages its broken into) is complete
export const isFormIncomplete = (state: SharedSignupState): boolean => getInvalidFields(state).length > 0;
